import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';
import { getUserProfile, createUserProfile, updateLastLogin } from '../services/firestoreService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function signInWithGoogle(signInAsOwner = false) {
    setError(null);
    localStorage.setItem('preferredUserType', signInAsOwner ? 'owner' : 'user');
    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  }

  function logout() {
    localStorage.removeItem('preferredUserType');
    return signOut(auth);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const preferredType = localStorage.getItem('preferredUserType') || 'user';
          let profile = await getUserProfile(user.uid, preferredType);
          if (profile) {
            await updateLastLogin(user.uid, profile.type);
          } else {
            profile = await createUserProfile(user.uid, {
              email: user.email,
              name: user.displayName,
              photoURL: user.photoURL,
            }, preferredType);
          }
          setUserProfile(profile);
          setUserType(profile.type);
        } catch (err) {
          setError('Failed to sync profile. Please refresh and try again.');
        }
      } else {
        setUserProfile(null);
        setUserType(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    userType,
    loading,
    error,
    isOwner: userType === 'owner',
    isUser: userType === 'user',
    signInWithGoogle,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
